import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { TableChart, Toc } from '@mui/icons-material';
import Case from './Case';
import DataView from './DataView';
import BasePage from './BasePage';
import useStyles from './Case.styles';
import { useCasePageReady } from '../../db/hooks';
import { CaseDataProvider } from '../../db/CaseDataContext';

function ViewToggle({ caseId, isDataView }) {
  const classes = useStyles();
  const navigate = useNavigate();

  if (isDataView) {
    return (
      <Tooltip title="Jury box view">
        <IconButton
          onClick={() => navigate(`/case/${caseId}`)}
          size="small"
        >
          <TableChart className={classes.actionButton} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Data view">
      <IconButton
        onClick={() => navigate(`/case/${caseId}/data`)}
        size="small"
      >
        <Toc className={classes.actionButton} />
      </IconButton>
    </Tooltip>
  );
}

function CaseContentLoading() {
  const classes = useStyles();
  // Case identity passed via navigation state from the case list — shows
  // immediately, before local data is decrypted. Absent on hard reloads.
  const { caseName, caseNumber } = useLocation().state || {};

  return (
    <div className={classes.caseLoading} role="status" aria-live="polite">
      <CircularProgress size={64} />
      {caseName ? (
        <>
          <Typography variant="h5">
            <b>{caseName}</b>
          </Typography>
          {!!caseNumber && (
            <Typography variant="h6" color="textSecondary">
              {caseNumber}
            </Typography>
          )}
          <Typography variant="body1" color="textSecondary">
            Loading case...
          </Typography>
        </>
      ) : (
        <Typography variant="h6" color="textSecondary">
          Loading case...
        </Typography>
      )}
    </div>
  );
}

const CasePage = ({ id: caseId }) => {
  const classes = useStyles();
  const { isLoading, caseData, jurors, redirectTo } = useCasePageReady(caseId);
  const location = useLocation();
  const navigate = useNavigate();
  const isDataView = location.pathname.endsWith('/data');

  useEffect(() => {
    if (!redirectTo) return;
    // The case we were viewing under a local id got synced and re-keyed
    // under its server id (see SyncEngine.updateLocalCaseIdToServerId).
    // Follow it so the page doesn't render as empty/default.
    navigate(`/case/${redirectTo}${isDataView ? '/data' : ''}`, { replace: true, state: location.state });
  }, [redirectTo, isDataView, navigate, location.state]);

  return (
    <CaseDataProvider
      caseId={caseId}
      caseData={caseData}
      jurors={jurors}
      disabled={isLoading}
    >
      <BasePage
        caseId={caseId}
        isPageLoading={isLoading}
        topNav={<ViewToggle caseId={caseId} isDataView={isDataView} />}
      >
        {isLoading ? (
          <CaseContentLoading />
        ) : (
          <>
            <div
              className={`${classes.viewPanel} ${isDataView ? classes.viewPanelHidden : ''}`}
              aria-hidden={isDataView}
            >
              <Case id={caseId} />
            </div>
            <div
              className={`${classes.viewPanel} ${isDataView ? '' : classes.viewPanelHidden}`}
              aria-hidden={!isDataView}
            >
              <DataView id={caseId} />
            </div>
          </>
        )}
      </BasePage>
    </CaseDataProvider>
  );
};

export default CasePage;
